import { useEffect, useMemo, useState } from "react";
import { AccessibilityInfo } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { useFeedback } from "@louise/moon-ui";
import { useBadgeCelebration } from "@src/packages/badges/BadgeCelebrationProvider";
import type { BadgeUnlockEvent } from "@src/packages/badges/badge-events";
import { getBadgeDefinition } from "../screens/YouScreen/data";
import {
  BadgeUnlockCarouselDialog,
  BadgeUnlockDialog,
} from "../screens/YouScreen/sections/MyPassport";
import type { Badge } from "../screens/YouScreen/types";
import type { RootStackParamList } from "../../App";

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled().then(setReducedMotion);
    const subscription = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReducedMotion,
    );

    return () => subscription.remove();
  }, []);

  return reducedMotion;
}

function badgeForEvent(event: BadgeUnlockEvent): Badge | null {
  const definition = getBadgeDefinition(event.badgeId);
  if (!definition) return null;

  return {
    ...definition,
    unlocked: true,
    unlockedAt: event.occurredAt,
    description: event.copy.body,
  };
}

export default function BadgeCelebrationOverlay() {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { activeEvent, pendingEvents, dismissActive, dismissAll } =
    useBadgeCelebration();
  const { hapticsEnabled, soundEnabled } = useFeedback();
  const reducedMotion = useReducedMotion();
  const event = activeEvent?.event ?? null;
  const badge = useMemo(() => (event ? badgeForEvent(event) : null), [event]);

  /**
   * Every pending badge this build knows how to draw, paired with its event.
   *
   * An unknown id is dropped here rather than rendered as an ad-hoc milestone,
   * which also keeps it from being counted in "3 badges unlocked" and then not
   * appearing.
   */
  const renderable = useMemo(
    () =>
      pendingEvents.flatMap((pending) => {
        const known = badgeForEvent(pending.event);
        return known ? [{ badge: known, event: pending.event }] : [];
      }),
    [pendingEvents],
  );

  useEffect(() => {
    // Unknown server badge IDs are not safe to render as ad-hoc milestones.
    // Dismiss them so they cannot block a later supported celebration.
    if (event && !badge) dismissActive();
  }, [badge, dismissActive, event]);

  const quiet =
    reducedMotion ||
    event?.copy.celebrationMode === "quiet" ||
    (!hapticsEnabled && !soundEnabled);
  const effectsOn =
    event?.copy.celebrationMode !== "quiet" && (hapticsEnabled || soundEnabled);

  const openPassport = (badgeId: string) => {
    navigation.navigate("Profile", {
      screen: "YouOverview",
      params: { badgeId },
    });
  };

  /**
   * Carry every badge the celebration held, starting where she was.
   *
   * With one id the passport opened that badge and the others were gone — she
   * had just seen three and landed on one, with no way back to them. The
   * carousel there reads the whole list, and the badge she was looking at is
   * put first so the dialog opens on the same card she tapped from.
   */
  const openPassportForAll = (badgeId: string) => {
    const ids = [
      badgeId,
      ...renderable.map((item) => item.badge.id).filter((id) => id !== badgeId),
    ];
    navigation.navigate("Profile", {
      screen: "YouOverview",
      params: { badgeIds: ids.join(",") },
    });
  };

  /**
   * Two or more at once become one dialog she can swipe, instead of a queue of
   * modals to clear. One badge keeps the dialog it has always had.
   */
  if (renderable.length > 1) {
    return (
      <BadgeUnlockCarouselDialog
        badges={renderable}
        visible
        onClose={dismissAll}
        onViewInPassport={(badgeId) => {
          dismissAll();
          openPassportForAll(badgeId);
        }}
        reducedMotion={quiet}
        celebrationEffectsEnabled={effectsOn}
      />
    );
  }

  return (
    <BadgeUnlockDialog
      badge={badge}
      event={badge ? (event ?? undefined) : undefined}
      visible={badge !== null}
      onClose={dismissActive}
      reducedMotion={quiet}
      celebrationEffectsEnabled={effectsOn}
      onViewInPassport={
        badge && event
          ? () => {
              dismissActive();
              openPassport(event.badgeId);
            }
          : undefined
      }
    />
  );
}
